import {
  CartLineReferenceType,
  LineItemType,
} from '@lambda/apis/src/cart/types';
import { useRouter } from 'next/router';
import { AppCtx } from 'pages/_app';
import { useContext } from 'react';
import { RC_DURATION } from '@lambda/reebelo';
import { useProductPageContext } from './hooks/useProductPageContext';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import { generateAddOnsNotes, generateProductNotes } from './hooks/notes';
import { isAccessoryBreadcrumb } from './hooks/helpers';
import {
  trackAddToCart,
  trackReebeloCareAdded,
} from '@/lib/analytics/trackProduct';
import AddToCartButton from './AddToCartButton';
import { AddToCartLinkProps } from './hooks/types';
import { ProductSource, ReebeloCareTrackRequest } from '@/lib/analytics/types';
import i18n from '@/settings/i18n';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import {
  getAccessoriesPath,
  isPurchaseFlowEnabled,
  rememberLastAddedSku,
} from '@/lib/purchase-flow';

const t = i18n.init('product');

const AddToCartLink = ({
  className,
  fullHeight,
  id,
  label,
  offer,
  selectedWarranty,
  testingId,
}: AddToCartLinkProps) => {
  const router = useRouter();
  // Needed for shipping notes (AU/NZ/CA)
  const { zipcodeStateData } = useContext(AppCtx);

  const {
    selectedSku,
    product,
    isFetchingSku,
    isOutOfStock,
    additionalInfo,
    isWarrantySelected,
    vendorDetails,
  } = useProductPageContext();
  const { addToCart, getLineItemInCart, cartLines } = useCartContext();
  const { customer } = useCustomerContext();

  const onAddToCartClick = async () => {
    if (!offer) {
      return;
    }

    const isAccessory = isAccessoryBreadcrumb(additionalInfo?.breadcrumb);

    // Warranty is added as its own line below; express shipping is chosen in
    // the cart.
    const notes = generateProductNotes({
      product,
      sku: selectedSku,
      offer,
      lineItem: getLineItemInCart(offer.id),
      zipcodeStateData,
      type: isAccessory ? 'ACCESSORY' : undefined,
    });

    await addToCart({
      typeId: offer.id,
      quantity: 1,
      notes,
      type: LineItemType.PRODUCT,
    }).then(async (updatedCart) => {
      // Add Product To Cart Tracking Event
      trackAddToCart({
        v2: {
          product,
          sku: selectedSku,
          offer,
          isOutOfStock,
        },
        properties: { source: ProductSource.PDP },
      });

      const updatedCartLines = updatedCart?.lines ?? cartLines;

      // Add RebeeloCare to Cart
      if (selectedWarranty) {
        const warrantyTrackingData: ReebeloCareTrackRequest = {
          entry_point: 'Product Page',
          reebelocare_id: selectedWarranty.id,
          device_psku: product.psku,
          device_vendor: offer.vendor.name,
          device_sku: offer.skuId,
          duration: selectedWarranty.duration.toString() as RC_DURATION,
        };

        const parentLineItem = updatedCartLines.find(
          (l) => l.typeId === offer.id && l.type === LineItemType.PRODUCT,
        );
        const isWarrantyInCart = updatedCartLines.find(
          (l) =>
            l.typeId === selectedWarranty.id &&
            l.type === LineItemType.WARRANTY,
        );

        // Check if parent line item exists and warranty is not already in cart
        if (!parentLineItem || isWarrantyInCart) {
          return;
        }

        const warrantyNotes = generateAddOnsNotes({
          product,
          sku: selectedSku,
          offer,
          lineItem: parentLineItem,
          selectedWarranty,
        });

        await addToCart({
          typeId: selectedWarranty.id,
          quantity: parentLineItem.quantity,
          notes: warrantyNotes,
          type: LineItemType.WARRANTY,
          reference: {
            type: CartLineReferenceType.CART_LINE,
            typeId: parentLineItem.id,
          },
        }).then(() => {
          // Add Warranty To Cart Tracking Event
          trackReebeloCareAdded(warrantyTrackingData);
        });
      }
    });

    // In the purchase flow, the device's accessories come before the cart.
    // The accessories step is for devices: an accessory goes straight to the
    // cart, and doesn't replace the device the progress bar links back to.
    if (
      isPurchaseFlowEnabled(customer?.email) &&
      !isAccessory &&
      selectedSku?.id
    ) {
      rememberLastAddedSku(selectedSku.id);
      router.push(getAccessoriesPath(selectedSku.id));

      return;
    }

    router.push('/cart');
  };

  // A shopper who ticked ReebeloCare waits for the current offer's plan, so
  // the device isn't added without the protection they chose.
  const isWarrantyPending =
    isWarrantySelected && vendorDetails.warranty.loading;

  const disabled = !offer || isFetchingSku || isOutOfStock || isWarrantyPending;

  return (
    <AddToCartButton
      isOutOfStock={isOutOfStock}
      testingId={testingId}
      vendorOfferId={offer?.vendorOfferId ?? ''}
      vendorSku={offer?.vendorSku ?? ''}
      psku={product.psku ?? ''}
      className={className}
      fullHeight={fullHeight}
      id={id}
      label={label ?? t`Add to Cart`}
      disabled={disabled}
      onClick={onAddToCartClick}
    />
  );
};

export default AddToCartLink;
